import {
  EMAIL_BODY_HEAD_STYLES,
  EMAIL_BODY_SHELL,
} from './email-template-design.constants';

const HEADER_PARTIAL = /\{\{\s*(?:>|&gt;)\s*email-header\s*\}\}/i;
const FOOTER_PARTIAL = /\{\{\s*(?:>|&gt;)\s*email-footer\s*\}\}/i;
const CONTENT_TD_OPEN =
  /<td[\s\S]*?style="[^"]*padding:\s*40px[^"]*"[\s\S]*?>/i;
const CONTENT_TD_CLOSE =
  /<\/td>\s*<\/tr>\s*<\/table>\s*<\/td>\s*<\/tr>\s*<\/table>\s*$/i;

export { EMAIL_BODY_SHELL };

/** ngx-editor HTML-encodes `>` in partial syntax — restore valid Handlebars. */
export function decodeEmailTemplatePartials(html: string): string {
  return html
    .replace(/\{\{\s*&gt;\s*email-header\s*\}\}/gi, '{{> email-header}}')
    .replace(/\{\{\s*&gt;\s*email-footer\s*\}\}/gi, '{{> email-footer}}');
}

export interface EmailTemplateParts {
  prefix: string;
  body: string;
  suffix: string;
}

function stripPartialTokens(html: string): string {
  return html
    .replace(/\{\{\s*(?:>|&gt;)\s*email-header\s*\}\}/gi, '')
    .replace(/\{\{\s*(?:>|&gt;)\s*email-footer\s*\}\}/gi, '')
    .trim();
}

function extractInnerBodyContent(middle: string): string {
  const trimmed = middle.trim();
  if (!trimmed) {
    return '';
  }

  const tdOpen = CONTENT_TD_OPEN.exec(trimmed);
  if (tdOpen?.index !== undefined) {
    const bodyStart = tdOpen.index + tdOpen[0].length;
    const afterOpen = trimmed.slice(bodyStart);
    const closeMatch = CONTENT_TD_CLOSE.exec(afterOpen);
    if (closeMatch?.index !== undefined) {
      return stripPartialTokens(afterOpen.slice(0, closeMatch.index).trim());
    }
    return stripPartialTokens(afterOpen.trim());
  }

  return stripPartialTokens(trimmed);
}

/** Extract only editable body — header/footer partial refs stay out of the editor. */
export function splitEmailTemplateForEdit(html: string): EmailTemplateParts {
  const source = decodeEmailTemplatePartials(html ?? '');
  const headerMatch = HEADER_PARTIAL.exec(source);
  const footerMatch = FOOTER_PARTIAL.exec(source);

  if (!headerMatch || footerMatch?.index === undefined) {
    return {
      prefix: '',
      body:
        extractInnerBodyContent(stripPartialTokens(source)) ||
        stripPartialTokens(source),
      suffix: '',
    };
  }

  const headerEnd = headerMatch.index + headerMatch[0].length;
  const footerStart = footerMatch.index;

  return {
    prefix: source.slice(0, headerEnd),
    body: extractInnerBodyContent(source.slice(headerEnd, footerStart)),
    suffix: source.slice(footerStart),
  };
}

function escapeStaticTitle(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Rewrites button patterns into fully inline-styled anchors so they render
 * correctly in email clients that strip <head><style> blocks and don't
 * support :has() selectors.
 *
 * Pattern A — editor CTA: <a href="…"><strong>…</strong></a>
 *   ngx-editor produces this when the user bolds a link; the live preview
 *   relies on a p > a:has(strong) CSS rule that no email client applies.
 *
 * Pattern B — hbs class button: <a class="button" href="…">…</a>
 *   Gmail rewrites/scopes class names so a.button { } stops matching,
 *   while any color:#fff it did apply makes the text invisible on white.
 */
export function inlineCtaButtonStyles(html: string): string {
  // Pattern A: <a …><strong>…</strong></a>  (editor CTA)
  let result = html.replace(
    /<a(\s[^>]*?)>\s*<strong>([\s\S]*?)<\/strong>\s*<\/a>/gi,
    (_match, attrs: string, inner: string) => {
      const text = inner.replace(/<\/?span[^>]*>/gi, '').trim();
      const cleanAttrs = attrs.replace(/\sstyle="[^"]*"/gi, '');
      return (
        `<a${cleanAttrs} style="display:inline-block;padding:14px 32px;` +
        `background-color:#132E56;color:#ffffff;text-decoration:none;` +
        `border-radius:10px;font-weight:700;font-size:15px;line-height:1.5;` +
        `margin:8px 0 24px;font-family:Arial,Helvetica,sans-serif;">${text}</a>`
      );
    },
  );

  // Pattern B: <a class="button" …>  without background-color already inlined
  result = result.replace(
    /<a(\s[^>]*?class="[^"]*\bbutton\b[^"]*"[^>]*?)>/gi,
    (match, attrs: string) => {
      if (/\sstyle="[^"]*background-color/i.test(attrs)) {
        return match;
      }
      const cleanAttrs = attrs.replace(/\sstyle="[^"]*"/gi, '');
      return (
        `<a${cleanAttrs} style="display:inline-block;padding:12px 24px;` +
        `background-color:#132E56;color:#ffffff;text-decoration:none;` +
        `border-radius:5px;font-size:14px;font-weight:600;line-height:1.5;">`
      );
    },
  );

  return result;
}

/** Full `.hbs` layout matching invite_user.hbs. */
export function buildEmailTemplateDocument(
  bodyHtml: string,
  templateTitle = 'Email',
): string {
  const title = escapeStaticTitle(templateTitle.trim() || 'Email');
  const body = inlineCtaButtonStyles(
    decodeEmailTemplatePartials(stripPartialTokens(bodyHtml)),
  );

  return `<!DOCTYPE html>
<html lang="en">

<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>{{website_name}} — ${title}</title>
  ${EMAIL_BODY_HEAD_STYLES}
</head>

<body style="margin:0;padding:0;background-color:#eef2f7;">
  {{> email-header}}

${EMAIL_BODY_SHELL.prefix}
${body}
${EMAIL_BODY_SHELL.suffix}

  {{> email-footer}}
</body>

</html>`;
}

export function mergeEmailTemplateBody(
  parts: EmailTemplateParts,
  templateTitle = 'Email',
): string {
  return buildEmailTemplateDocument(parts.body ?? '', templateTitle);
}

/** Rebuild full HTML with canonical invite_user layout for preview/send. */
export function normalizeEmailTemplateHtml(
  html: string,
  templateTitle = 'Email',
): string {
  const decoded = decodeEmailTemplatePartials(html ?? '');
  const trimmed = decoded.trim();

  const hasValidPartials =
    HEADER_PARTIAL.test(trimmed) &&
    FOOTER_PARTIAL.test(trimmed) &&
    !/\{\{\s*&gt;\s*email-(?:header|footer)\s*\}\}/i.test(trimmed);

  const hasFullDocument =
    /<!doctype/i.test(trimmed) || /<html[\s>]/i.test(trimmed);

  if (hasValidPartials && hasFullDocument) {
    return trimmed;
  }

  const editable = splitEmailTemplateForEdit(html).body;
  return buildEmailTemplateDocument(editable, templateTitle);
}
